import { useEffect, useState } from "react";
import api, { bioPhoto, formatApiError } from "@/lib/api";
import { Card } from "@/components/common";
import { Avatar } from "@/components/Avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, BookOpen, Mail, Phone } from "lucide-react";

export function BioDialog({ contact, onClose }) {
  const [bio, setBio] = useState(null);
  const [err, setErr] = useState(null);
  const userId = contact?.user_id;
  useEffect(() => {
    if (!userId) return;
    setBio(null); setErr(null);
    api.get(`/bios/${userId}`).then(({ data }) => setBio(data))
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Could not load this bio"));
  }, [userId]);
  // The bio payload carries its own question list, so labels always match the form.
  const blocks = (bio?.questions || []).map((q) => [q.key, q.label]);
  return (
    <Dialog open={!!contact} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto cc-scroll" data-testid="bio-dialog">
        <DialogHeader>
          <DialogTitle>{contact?.name}</DialogTitle>
          <DialogDescription>{[contact?.label, contact?.organization].filter(Boolean).join(" · ")}</DialogDescription>
        </DialogHeader>
        {err ? <p className="text-sm text-zinc-500" data-testid="bio-dialog-error">{err}</p>
          : !bio ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-cc-blue" /></div> : (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                {bio.photo_path
                  ? <img src={bioPhoto(userId)} alt={contact?.name} className="w-20 h-20 rounded-full object-cover border-2 border-cc-blue/20" data-testid="bio-dialog-photo" />
                  : <Avatar name={contact?.name} size={80} />}
                <div className="text-sm">
                  {contact?.email && <a href={`mailto:${contact.email}`} className="flex items-center gap-1.5 text-cc-blue hover:underline"><Mail className="w-3.5 h-3.5" />{contact.email}</a>}
                  {contact?.phone && <span className="flex items-center gap-1.5 text-zinc-500 mt-1"><Phone className="w-3.5 h-3.5" />{contact.phone}</span>}
                </div>
              </div>
              {blocks.filter(([k]) => (bio[k] || "").trim()).map(([k, label]) => (
                <div key={k} data-testid={`bio-block-${k}`}>
                  <div className="text-xs uppercase tracking-wide text-zinc-400">{label}</div>
                  <p className="text-sm text-zinc-700 whitespace-pre-wrap">{bio[k]}</p>
                </div>
              ))}
              {!blocks.some(([k]) => (bio[k] || "").trim()) && (
                <p className="text-sm text-zinc-500" data-testid="bio-dialog-empty">They haven't written their bio yet.</p>
              )}
            </div>
          )}
      </DialogContent>
    </Dialog>
  );
}

export function ContactCards({ contacts, title = "Your people", hint, testId = "contact-cards" }) {
  const [open, setOpen] = useState(null);
  if (!contacts?.length) return null;
  return (
    <Card title={title}>
      {hint && <p className="text-sm text-zinc-500 mb-3">{hint}</p>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={testId}>
        {contacts.map((c, i) => (
          <div key={`${c.label}-${c.email || c.name || i}`} className="border rounded-lg p-3.5 hover:border-cc-blue/50 transition-colors" data-testid={`contact-${(c.name || "unknown").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="text-[10px] uppercase tracking-wider text-cc-blue font-semibold">{c.label}</div>
            <div className="flex items-center gap-2.5 mt-1.5">
              <Avatar src={c.has_photo ? bioPhoto(c.user_id) : null} name={c.name} size={34} />
              <div className="min-w-0">
                {c.has_bio ? (
                  <button className="font-medium text-zinc-800 hover:text-cc-blue hover:underline text-left truncate flex items-center gap-1"
                    onClick={() => setOpen(c)} data-testid={`contact-bio-${(c.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    {c.name}<BookOpen className="w-3.5 h-3.5 text-cc-blue shrink-0" />
                  </button>
                ) : <div className="font-medium text-zinc-800 truncate">{c.name}</div>}
                {c.organization && <div className="text-xs text-zinc-400 truncate">{c.organization}</div>}
              </div>
            </div>
            {c.email && <a href={`mailto:${c.email}`} className="text-xs text-zinc-500 hover:text-cc-blue block mt-2 truncate">{c.email}</a>}
            {c.phone && <div className="text-xs text-zinc-400">{c.phone}</div>}
            {c.has_bio && <div className="text-[11px] text-cc-blue mt-1">Click their name to read their bio</div>}
          </div>
        ))}
      </div>
      {open && <BioDialog contact={open} onClose={() => setOpen(null)} />}
    </Card>
  );
}
